import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { ModalMode, ROLE_READ_ONLY } from '../../../stores/Administrators/AdministratorModalStore';
import EnvStore from '../../../stores/EnvStore';
import ConsoleStore, { ISOLATION_CHANNELS } from '../../../stores/ConsoleStore';
import FuseReadOnlyTextfield from '../../FuseControls/FuseReadOnlyTextfield';
import FuseReadOnlySwitch from '../../FuseControls/FuseReadOnlySwitch';
import MatchCriteria from '../MatchCriteria';
import PolicyModal from '../PolicyModal';
import PolicyModalHeaderLayout from '../PolicyModalHeaderLayout';
import FileActions from './FileActions';
import FileTypes from './FileTypes';
import SourceCountries from './SourceCountries';
import FileSize from './FileSize';
import AdditionalConditions from './AdditionalConditions';
import FileRulesStore from '../../../stores/Policy/FileRules/FileRulesStore';
import FileRuleAction from './FileRuleAction';
import { RULE_REC_TYPE } from '../../../constants';
import FuseAlert from '../../FuseControls/FuseAlert';

class FileRuleModal extends Component {
    static propTypes = {
        store: FileRulesStore.PropType.isRequired,
        env: EnvStore.PropType.isRequired,
        console: ConsoleStore.PropType,
        renderName: PropTypes.func,
        showPreviewAction: PropTypes.bool,
        showMalwareCondition: PropTypes.bool,
        showDetectorSetsCondition: PropTypes.bool,
        scanFileSize: PropTypes.number.isRequired,
        fileScanType: PropTypes.string.isRequired,
        itemNameFull: PropTypes.string.isRequired,
        sourceCountriesName: PropTypes.string.isRequired,
        ruleType: PropTypes.string.isRequired,
    };

    renderHeader = () => {
        const { env, renderName, showDetectorSetsCondition } = this.props;
        const { policyModal } = this.props.store.policyTable;

        const store = policyModal.header;
        const readOnly = [ModalMode.VIEW, ModalMode.DELETE].includes(policyModal.mode) || store.isDefault;
        const footer = store.isDefault
            ? 'The Default Rule is system-generated, cannot be deleted, renamed, ' +
            'or disabled and is always the lowest priority.' : undefined;
        const isDetectorsSetEmpty = policyModal.sections.additionalConditions.isDetectorsLoadedEmpty();

        return <PolicyModalHeaderLayout
            title="Summary"
            readOnly={readOnly}
            footer={footer}
            alert={showDetectorSetsCondition && isDetectorsSetEmpty ? <FuseAlert style={{ marginBottom: 20 }}>
                <b>No detector sets defined in the DLP application. </b>
                You must configure at least 1 detector set in the “Data Loss Prevention“
                application in order to save File DLP Rules that scan files.
            </FuseAlert> : null}
        >
            <FuseReadOnlyTextfield
                label="Name"
                store={store.name}
                readOnlyValue={renderName({ isDefault: store.isDefault, name: store.name.value })}
                placeholder={showDetectorSetsCondition ? 'Enter File DLP Name' : 'Enter rule name'}
                error={!store.name.isValid && store.name.isTouched}
                onBlur={store.onNameFieldFocusChange}
                showRequired={!readOnly}
                required={!readOnly}
                qa="entity-name"
            />

            <FuseReadOnlyTextfield
                label="Description"
                store={store.description}
                readOnlyValue={store.description.value && store.description.value.trim() || '—'}
                placeholder={showDetectorSetsCondition ? 'Enter File DLP Description' : 'Enter rule description'}
                qa="entity-description"
            />

            <FuseReadOnlySwitch
                header="Rule Status"
                tooltip="The status will determine if the Rule is available to live users or not."
                tooltipProps={env.screen.isMobile || readOnly ? { position: 'right' } : {}}
                store={store.status}
                label={store.status.isTrue ? 'Enabled' : 'Disabled'}
                qa="status-switch"
            />
        </PolicyModalHeaderLayout>;
    };

    getSectionsInfo = (matchCriteria, categories, isDefault) => {
        const { isolationChannelsSelect } = matchCriteria;
        const { fileActions, fileTypes, sourceCountries, fileSize } = matchCriteria.extraSections;
        const hasTapLicense = this.props.console.hasTapUrlIsolationLicense();

        return [
            { name: 'Browsing Roles' },
            {
                name: 'Isolation Channels',
                canHide: false,
                isEmpty: () => (isDefault ? false : isolationChannelsSelect.isEmpty()),
                clear: () => isolationChannelsSelect.clear(),
                isValid: () => (isDefault ? true : !isolationChannelsSelect.isEmpty()),
            },
            ...hasTapLicense ? [] : [{
                extraSection: (readOnly) => <FileActions store={fileActions} readOnly={readOnly} />,
                extraSectionName: 'File Actions'
            }],
            {
                name: 'Categories',
                canHide: true,
                isRequired: true,
                isEmpty: () => categories.isEmpty(),
                clear: () => categories.clear(),
                isValid: () => !categories.isEmpty(),
            },
            {
                extraSection: (readOnly) => <FileTypes store={fileTypes} readOnly={readOnly} />,
                extraSectionName: 'File Types',
                canHide: true,
                isEmpty: () => fileTypes.isEmpty(),
                clear: () => fileTypes.clear(),
                isValid: () => !fileTypes.isEmpty(),
            },
            {
                extraSection: (readOnly) => <SourceCountries
                    store={sourceCountries}
                    readOnly={readOnly}
                    title={this.props.sourceCountriesName}
                />,
                extraSectionName: `${this.props.sourceCountriesName} Countries`,
                canHide: true,
                isEmpty: () => sourceCountries.isEmpty(),
                clear: () => sourceCountries.clear(),
                isValid: () => !sourceCountries.isEmpty(),
            },
            {
                extraSection: (readOnly) => <FileSize store={fileSize} readOnly={readOnly} />,
                extraSectionName: 'Max File Size',
                canHide: true,
                isEmpty: () => fileSize.isEmpty(),
                clear: () => fileSize.clear(),
                isValid: () => !fileSize.isEmpty(),
            },
        ];
    }

    constructor(props) {
        super(props);
        const { policyModal } = this.props.store.policyTable;
        policyModal.sections.matchCriteria.setFilterChannelsFunc(this.getChannelsOptionFilterFunc());
    }

    getChannelsOptionFilterFunc() {
        const { console } = this.props;
        return (op) => (op === ISOLATION_CHANNELS.SAAS_ISOLATION || console.filterIsolationChannelBasedOnLicense(op));
    }

    render() {
        const { policyModal } = this.props.store.policyTable;
        const {
            console,
            itemNameFull,
            showPreviewAction,
            showMalwareCondition,
            showDetectorSetsCondition,
            scanFileSize,
            fileScanType,
            sourceCountriesName,
            ruleType,
        } = this.props;
        const {
            matchCriteria,
            categories,
            action,
            additionalConditions,
        } = policyModal.sections;

        const matchCriteriaTxt = 'Match Criteria for the Default Rule includes all Users, '.concat(
            console.hasTapUrlIsolationLicense() ?
                'URLs, File Types, Countries and File Sizes, and cannot be changed.' :
                `Isolation Channels, URLs, File Types, ${sourceCountriesName} Countries
            and File Sizes for both uploads and downloads, and cannot be changed.`);

        const readOnly = [ModalMode.VIEW, ModalMode.DELETE].indexOf(policyModal.mode) > -1 ||
            console.authority === ROLE_READ_ONLY.value;
        const isDefault = policyModal.getValue().isDefault;
        const conditionsTitlePrefix = ruleType === RULE_REC_TYPE.FILE_THREAT_RULES ? 'Threat Scan' : 'DLP Scan';

        return <PolicyModal
            titleItemName={itemNameFull}
            readOnly={console.authority === ROLE_READ_ONLY.value}
            store={policyModal}
        >
            {this.renderHeader()}

            <MatchCriteria
                store={matchCriteria}
                categoriesStore={categories}
                readOnly={isDefault || readOnly}
                sectionsInfo={this.getSectionsInfo(matchCriteria, categories, isDefault)}
                footerText={matchCriteriaTxt}
            />

            <FileRuleAction
                store={action}
                readOnly={readOnly}
                showPreviewAction={showPreviewAction}
                ruleType={ruleType}
                isTapLicense={console.hasTapUrlIsolationLicense()}
            />

            {action.action.getValue() === 'SCAN' && !console.hasTapUrlIsolationLicense() ?
                <AdditionalConditions
                    store={additionalConditions}
                    readOnly={readOnly}
                    showMalwareCondition={showMalwareCondition}
                    showDetectorSetsCondition={showDetectorSetsCondition}
                    scanFileSize={scanFileSize}
                    fileScanType={fileScanType}
                    supportedFileTypes={matchCriteria.extraSections.fileTypes.supportedFileTypes}
                    title={`${conditionsTitlePrefix} Conditions`}
                /> : null}
        </PolicyModal>;
    }
}

export default inject('env', 'console')(observer(FileRuleModal));
